import React from 'react';
import { useChatQueueActions } from '../../../store/hooks';
import { CtaActionIcon } from './icon';

// Explicit field-by-field mapping. Only fields with a dedicated JSX block
// below render — every other key in ``structured`` (recommendation, content,
// summary, ticker, internal debug fields, future agent additions, …) is
// silently ignored so the page never leaks unintended payload to the user.
// Add a new field by writing a new render block here, never by listing keys.

function isObj(v) {
    return v && typeof v === 'object' && !Array.isArray(v);
}

function buildCtaPrompt(item) {
    if (!isObj(item)) return '';
    const label = typeof item.label === 'string' ? item.label.trim() : '';
    const description = typeof item.description === 'string' ? item.description.trim() : '';
    if (label && description) return `${label} — ${description}`;
    return label || description;
}

function CtaItems({ cta }) {
    const { send: sendChat } = useChatQueueActions();
    if (!isObj(cta)) return null;
    const items = Array.isArray(cta.cta_items) ? cta.cta_items : [];
    if (!cta.cta_intro && !items.length) return null;
    return (
        <div className="space-y-[16px]">
            {cta.cta_intro && <p className="text-[16px] font-bold text-[#111827] m-0">{cta.cta_intro}</p>}
            {!!items.length && (
                <ul className="flex flex-wrap gap-[16px] list-none p-0 m-0">
                    {items.map((it, i) => {
                        const prompt = buildCtaPrompt(it);
                        const actionable = !!prompt;
                        const hasDescription = !!it.description;
                        return (
                            <li
                                key={i}
                                role={actionable ? 'button' : undefined}
                                tabIndex={actionable ? 0 : undefined}
                                onClick={actionable ? () => sendChat(prompt, 'analysis-cta') : undefined}
                                onKeyDown={
                                    actionable
                                        ? (e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                sendChat(prompt, 'analysis-cta');
                                            }
                                        }
                                        : undefined
                                }
                                className={`min-w-[180px] flex-1 border border-[#ECECED] rounded-[16px] px-[16px] py-[12px] flex ${hasDescription ? 'flex-col justify-between' : 'flex-row items-center justify-between'} transition-colors ${actionable ? 'cursor-pointer hover:bg-[#F8FAFC] hover:border-[#CBD5E1] focus:outline-none focus:ring-2 focus:ring-[#6366F1]/40' : ''}`}
                            >
                                <div>
                                    <p className="text-[16px] font-medium text-[#111827] m-0">{it.label}</p>
                                    {hasDescription && <p className="text-[14px] text-[#636369] leading-[20px] m-0 mt-[4px]">{it.description}</p>}
                                </div>
                                {actionable && (
                                    <div className={`flex justify-end${hasDescription ? ' mt-[8px]' : ''}`}>
                                        <CtaActionIcon />
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

export default function StructuredExtras({ structured }) {
    if (!isObj(structured)) return null;

    const cta = structured.cta;
    const hasCtaContent = isObj(cta) && (
        (typeof cta.cta_intro === 'string' && cta.cta_intro.trim())
        || (Array.isArray(cta.cta_items) && cta.cta_items.length > 0)
    );

    if (!hasCtaContent) return null;

    return (
        <section className="bg-white border border-[#ECECED] rounded-[16px] p-[16px] sm:px-[32px] sm:py-[24px] md:px-[48px] md:py-[32px] flex flex-col gap-[16px]">
            <CtaItems cta={cta} />
        </section>
    );
}
